import { defineStore } from 'pinia'

import type { Basemap } from 'src/models/Basemap'
import { changeLocale, getLocale } from '@helpers/language/TranslationHelper'
import { useStationsStore } from './stations'
import type { AvailableLanguage } from 'src/helpers/language/AvailableLanguages'
import i18n from 'src/i18n'
import { UnitsService } from 'src/services/UnitsService'
import { AvailableUnits } from 'src/helpers/units/AvailableUnits'

import { socket } from '../services/SocketIOService'
import { useMeteoMapStore } from './meteoMap'
import { useMosaicStore } from './mosaic'
import { useFireMapStore } from './fireMap'
import { useSimulationMapStore } from './simulationMapStore'
import { Dayjs } from 'dayjs'
import { DatadogService } from '@services/DatadogService'

export const useAppStore = defineStore('app', {
  state: () => ({
    isLoading: false,
    initialized: false,
    basemaps: [
      // { type: 'raster', title: 'El que no le gusta a Alberto', image: 'map-relief.jpg', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ' },
      // { type: 'vector', title: 'World Basemap', image: 'map-satellite.jpg', url: 'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer', attribution: '' },
      {
        id: 'relieve',
        type: 'vectorBasemap',
        title: i18n.t('header.panels.basemaps.relief'),
        image: 'map-relief-new.jpg',
        key: 'e86bdf88c15b4e9391d75363909c2677',
        attribution: ''
      },
      // { id: 'coastline', type: 'vectorBasemap', title: 'Coastline', image: 'map-light-gray-labels.jpg', key: '7ac23232c0f34b16ba1f75c0fe8bb13a', attribution: '' },
      {
        id: 'light-gray',
        type: 'vectorBasemap',
        title: i18n.t('header.panels.basemaps.grayScale'),
        image: 'map-light-gray-new.jpg',
        key: 'ArcGIS:LightGray:Base',
        attribution: ''
      },
      // { id: 'light-gray-labels', type: 'vectorBasemap', title: 'Light Gray (labels)', image: 'map-light-gray-labels.jpg', key: 'ArcGIS:LightGray:Labels', attribution: '' },
      // { type: 'tiledMap', title: 'Relieve2', image: 'map-light-gray-labels.jpg', url: 'https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer', attribution: '' },
      {
        id: 'imagery',
        type: 'vectorBasemap',
        title: i18n.t('header.panels.basemaps.satelite'),
        dark: true,
        image: 'map-imagery.jpg',
        key: 'ArcGIS:Imagery:Standard',
        attribution: ''
      },
      {
        id: 'openstreetmap',
        type: 'tileLayer',
        title: 'OpenStreetMap',
        image: 'map-openstreetmap.jpg',
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: ''
      }
    ] as Basemap[],
    language: undefined as AvailableLanguage | undefined,
    units: undefined as AvailableUnits | undefined,

    // Global map options
    isPerfilVerticalOpen: false,
    isMeteogramaOpen: false,
    isENSOpen: false,

    sliderSelectedDate: undefined as Dayjs | undefined,
    sliderStartDate: undefined as Dayjs | undefined,
    sliderEndDate: undefined as Dayjs | undefined
  }),

  getters: {},

  actions: {
    init() {
      DatadogService.start()
      this.SET_LOADING(true)
      this.SET_LANGUAGE(getLocale())
      this.SET_UNITS(UnitsService.getUnits())
      useMeteoMapStore().SET_USER_MAP_OPTIONS()
      useMeteoMapStore().SET_USER_VIEW_OPTIONS()
      useMosaicStore().SET_USER_VIEW_OPTIONS()
      useMosaicStore().SET_USER_MAP_OPTIONS()
      useFireMapStore().SET_USER_VIEW_OPTIONS()
      useFireMapStore().SET_USER_MAP_OPTIONS()
      useSimulationMapStore().SET_USER_MAP_OPTIONS()
      useSimulationMapStore().SET_USER_VIEW_OPTIONS()
      socket.connect()
      const promises = []
      promises.push(useStationsStore().fetchAllStations())
      Promise.all(promises)
        .then(() => {
          this.SET_LOADING(false)
          this.SET_INITIALIZED(true)
        })
        .catch((error) => {
          console.error(`Error init data ${error}`)
          this.SET_LOADING(false) // TODO: Saltar error
        })
      // }
    },

    async SET_LANGUAGE(language: AvailableLanguage) {
      this.language = language
      await changeLocale(language)
    },

    SET_LOADING(isLoading: boolean) {
      this.isLoading = isLoading
    },

    SET_UNITS(units: AvailableUnits) {
      this.units = units
      UnitsService.changeUnits(units)
    },

    SET_INITIALIZED(isInitialized: boolean) {
      this.initialized = isInitialized
    },

    SET_IS_METEOGRAMA_OPEN(isOpen: boolean) {
      this.isMeteogramaOpen = isOpen
    },

    SET_IS_PERFIL_VERTICAL_OPEN(isOpen: boolean) {
      this.isPerfilVerticalOpen = isOpen
    }
  }
})
